import { EAuthenticationType, ICommonSettings } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import _ from "lodash";
import * as React from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

/** The fields inside this dialog, for the settings search. See `IConfigFieldProps.searchTerms`. */
export const REST_API_SETTINGS_SEARCH_TERMS: string[] = [
  "Base URL",
  "Authentication Type",
  "Client ID key name",
  "Client ID",
  "Client Secret key name",
  "Client Secret",
  "Username",
  "Password"
];

export interface IRestApiConnectionSetting {
  id: string;
  name: string;
  baseUrl?: string;
  authType?: EAuthenticationType;
  ClientIdKeyName?: string;
  ClientId?: string;
  ClientSecretKeyName?: string;
  ClientSecret?: string;
  BasicAuthUsername?: string;
  BasicAuthPassword?: string;
}

export interface IConfigRestApiSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingRestApiSetting?: IRestApiConnectionSetting;
}

export interface IConfigRestApiSettingsProps {
  formInputName: string[];
  closeModal: () => void;
  showModal: boolean;
  editingRestApiSettingId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigRestApiSettings extends React.PureComponent<
  IConfigRestApiSettingsProps,
  IConfigRestApiSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigRestApiSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingRestApiSetting: undefined
    };
  }

  componentDidMount() {
    this.loadRestApiSettings();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigRestApiSettingsProps) {
    if (prevProps.editingRestApiSettingId && !this.props.editingRestApiSettingId) {
      this.formRef.current?.resetFields();
    }
    if (prevProps.editingRestApiSettingId !== this.props.editingRestApiSettingId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadRestApiSettings();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadRestApiSettings();
    }
  }

  loadRestApiSettings = () => {
    if (!this.props.editingRestApiSettingId) return;

    const restApiSettings = _.cloneDeep((this.props.commonSettings as any).restApiConnectionSettings) as
      | IRestApiConnectionSetting[]
      | undefined;

    const editingRestApiSetting = restApiSettings?.find((setting) => setting.id === this.props.editingRestApiSettingId);

    if (editingRestApiSetting) {
      this.setState({ editingRestApiSetting }, () => {
        this.formRef.current?.resetFields();
        this.formRef.current?.setFieldsValue(editingRestApiSetting);
      });
    }
  };

  existingNameValidator = async (_rule: any, value: string) => {
    const restApiSettings = (this.props.commonSettings as any).restApiConnectionSettings as
      | IRestApiConnectionSetting[]
      | undefined;

    const isSettingAlreadyExist =
      restApiSettings?.some((x) => x.name === value && x.id !== this.props.editingRestApiSettingId) ||
      value === "Default";

    if (isSettingAlreadyExist) throw new Error("REST API Setting Name already exists.");
  };

  onFinish = async (data: any) => {
    this.saveStatus.start();

    const obj: IRestApiConnectionSetting = {
      id:
        this.props.editingRestApiSettingId && this.props.editingRestApiSettingId !== ""
          ? this.props.editingRestApiSettingId
          : Guid.create().toString(),
      name: data.name,
      baseUrl: data.baseUrl?.trim(),
      authType: data.authType,
      ClientIdKeyName: data.ClientIdKeyName?.trim(),
      ClientId: data.ClientId?.trim(),
      ClientSecretKeyName: data.ClientSecretKeyName?.trim(),
      ClientSecret: data.ClientSecret?.trim(),
      BasicAuthUsername: data.BasicAuthUsername?.trim(),
      BasicAuthPassword: data.BasicAuthPassword?.trim()
    };

    if (this.props.editingRestApiSettingId) {
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "restApiConnectionSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "restApiConnectionSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (_errorInfo: any) => {};

  deleteRestApiSetting = () => {
    if (!this.props.editingRestApiSettingId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingRestApiSettingId || "",
      configKey: "restApiConnectionSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`REST API setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="REST API setting"
        secondHeader="REST API setting"
        width={640}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingRestApiSettingId ? this.deleteRestApiSetting : undefined}
            deleteTestId="restApiSettingDeleteButton"
            submitTestId="restApiSettingCreateButton"
            submitLabel={this.props.editingRestApiSettingId ? "UPDATE" : "CREATE"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configRestApiForm"
          />
        }
      >
        <Form
          id="configRestApiForm"
          name="restApiSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription("appsettings_restapisettingpop_up_description")}
          >
            <div>
              <ConfigField label="Name" required tooltipContentKey="appsettings_restapisettingpop_up_name">
                <Form.Item
                  name="name"
                  rules={[
                    { required: true, message: "Please enter a REST API Setting Name." },
                    {
                      pattern: /^[A-Za-z][A-Za-z0-9 ]+$/,
                      message: "REST API Setting Name is invalid"
                    },
                    { validator: this.existingNameValidator }
                  ]}
                >
                  <KuikaInput size="md" data-testid="restApiSettingNameInput" autoFocus />
                </Form.Item>
              </ConfigField>

              <ConfigField label="Base URL" required tooltipContentKey="appsettings_restapisettingpop_up_baseurl">
                <Form.Item
                  name="baseUrl"
                  rules={[
                    { required: true, message: "Please enter a Base URL." },
                    {
                      type: "url",
                      message: "Base URL is not valid"
                    }
                  ]}
                >
                  <KuikaInput
                    size="md"
                    data-testid="restApiSettingBaseUrlInput"
                    placeholder="https://api.example.com"
                  />
                </Form.Item>
              </ConfigField>

              <ConfigField
                label="Authentication Type"
                required
                tooltipContentKey="appsettings_restapisettingpop_up_authtype"
              >
                <Form.Item
                  name="authType"
                  rules={[{ required: true, message: "Please select an Authentication Type." }]}
                >
                  <KuikaSelectbox
                    size="md"
                    data-testid="restApiSettingAuthTypeDropdown"
                    // The ids go on the option ROW (`testId`), not inside its label: the selected
                    // value renders the label too, so an id there matched twice once picked.
                    options={[
                      {
                        value: EAuthenticationType.NoAuth,
                        label: "No Auth",
                        searchLabel: "No Auth",
                        testId: "noauthOption"
                      },
                      {
                        value: EAuthenticationType.KuikaAuth,
                        label: "Bearer",
                        searchLabel: "Bearer",
                        testId: "bearerOption"
                      },
                      {
                        value: EAuthenticationType.Basic,
                        label: "Basic",
                        searchLabel: "Basic",
                        testId: "basicOption"
                      },
                      {
                        value: EAuthenticationType.ClientIdSecret,
                        label: "API Key",
                        searchLabel: "API Key",
                        testId: "apikeyOption"
                      }
                    ]}
                  />
                </Form.Item>
              </ConfigField>

              <Form.Item noStyle shouldUpdate={(prev, cur) => prev.authType !== cur.authType}>
                {({ getFieldValue }) => {
                  const authType = getFieldValue("authType") as EAuthenticationType;

                  if (authType === EAuthenticationType.ClientIdSecret) {
                    return (
                      <>
                        <ConfigField
                          label="Client ID key name"
                          required
                          tooltipContentKey="appsettings_restapisettingpop_up_clientidkeyname"
                        >
                          <Form.Item
                            name="ClientIdKeyName"
                            rules={[
                              { required: true, message: "Please enter Client ID key name." },
                              { whitespace: true, message: "Client ID key name is invalid." }
                            ]}
                          >
                            <KuikaInput size="md" placeholder="client_id" />
                          </Form.Item>
                        </ConfigField>

                        <ConfigField
                          label="Client ID"
                          required
                          tooltipContentKey="appsettings_restapisettingpop_up_clientid"
                        >
                          <Form.Item
                            name="ClientId"
                            rules={[
                              { required: true, message: "Please enter Client ID." },
                              { whitespace: true, message: "Client ID is invalid." }
                            ]}
                          >
                            <KuikaInput size="md" />
                          </Form.Item>
                        </ConfigField>

                        <ConfigField
                          label="Client Secret key name"
                          required
                          tooltipContentKey="appsettings_restapisettingpop_up_clientsecretkeyname"
                        >
                          <Form.Item
                            name="ClientSecretKeyName"
                            rules={[
                              { required: true, message: "Please enter Client Secret key name." },
                              { whitespace: true, message: "Client Secret key name is invalid." }
                            ]}
                          >
                            <KuikaInput size="md" placeholder="client_secret" />
                          </Form.Item>
                        </ConfigField>

                        <ConfigField
                          label="Client Secret"
                          required
                          tooltipContentKey="appsettings_restapisettingpop_up_clientsecret"
                        >
                          <Form.Item
                            name="ClientSecret"
                            rules={[
                              { required: true, message: "Please enter Client Secret." },
                              { whitespace: true, message: "Client Secret is invalid." }
                            ]}
                          >
                            <KuikaInput size="md" />
                          </Form.Item>
                        </ConfigField>
                      </>
                    );
                  }

                  if (authType === EAuthenticationType.Basic) {
                    return (
                      <>
                        <ConfigField
                          label="Username"
                          required
                          tooltipContentKey="appsettings_restapisettingpop_up_basicauth_username"
                        >
                          <Form.Item
                            name="BasicAuthUsername"
                            rules={[
                              { required: true, message: "Please enter Basic Auth username." },
                              { whitespace: true, message: "Basic Auth username is invalid." }
                            ]}
                          >
                            <KuikaInput size="md" />
                          </Form.Item>
                        </ConfigField>

                        <ConfigField
                          label="Password"
                          required
                          tooltipContentKey="appsettings_restapisettingpop_up_basicauth_password"
                        >
                          <Form.Item
                            name="BasicAuthPassword"
                            rules={[
                              { required: true, message: "Please enter Basic Auth password." },
                              { whitespace: true, message: "Basic Auth password is invalid." }
                            ]}
                          >
                            <KuikaInput size="md" type="password" />
                          </Form.Item>
                        </ConfigField>
                      </>
                    );
                  }
                  return null;
                }}
              </Form.Item>
            </div>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
